
import { Vue, Component } from 'vue-property-decorator';
import moment from 'moment';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import TechnologistOrderFormInputs from '@/components/views/NewProjectView/Order/TechnologistOrderFormInputs.vue';
import TechnologistOrderFormOrders from '@/components/views/NewProjectView/Order/TechnologistOrderFormOrders.vue';
import TechnologistOrderFormDates from '@/components/views/NewProjectView/Order/TechnologistOrderFormDates.vue';
import { TechnologistFormOrder } from '@/interfaces/components/projectNew/TechnologistFormOrder';
import {
    technologistOrderFormConfiguration,
    prepareTechnologistOrderFormData,
    refreshTechnologistOrderFormOptions,
} from '@/helpers/OrderHelper';
import { TechnologistFormField } from '@/interfaces/components/projectNew/TechnologistFormField';
import { TechnologistForm } from '@/interfaces/components/projectNew/TechnologistForm';
import NextStep from '@/models/NextStep';
import ProductSystem from '@/models/ProductSystem';
import { ProductSystemRepository } from '@/repositories/ProductSystemRepository';
import { UserRepository } from '@/repositories/UserRepository';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { getFirstAvailableDeliveryDate } from '@/helpers/DeliveryDateHelper';
import { hasCommentOrAttachment } from '@/helpers/OrderValidationHelper';
import { projectConnectionNameRegex } from '@/regex/formRegex';
import { CommonUserGroups } from '@/enums/global/CommonUserGroups';
import InternalOrderWithoutConfigurationAPI from '@/api/InternalOrderWithoutConfigurationAPI';

const ROLTEK_GROUP_IDS = [1, 2];
const REQUIRED_ANY_RIGHT = ['CREATE_TECHNOLOGIST_ORDER', 'REVIEW_PREORDERS'];

interface ClientOption {
    key: string;
    clientId: string;
    userGroupId: number;
    name: string;
    label: string;
    paymentTypeId: string | null;
    assignedUserId: string | null;
    assignedUserName: string;
    contactPersonId: string | null;
}

@Component({
    name: 'InternalOrderWithoutConfigurationPopup',
    components: {
        TechnologistOrderFormInputs,
        TechnologistOrderFormOrders,
        TechnologistOrderFormDates,
    },
})
export default class InternalOrderWithoutConfigurationPopup extends Vue {
    private visible: boolean = false;
    private submitting: boolean = false;

    private formData: Partial<Record<keyof TechnologistForm, any>> = {};
    private orders: TechnologistFormOrder[] = [];
    private errors: { [key: string]: string } = {};
    private orderErrors: { [key: string]: { system: boolean; quantity: boolean } } = {};
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private pendingColorTarget: 'general' | number | null = null;

    // Fields the popup renders itself (client selector and an editable project connection).
    private hiddenFields: string[] = ['client', 'projectConnection', 'commercialist'];

    private clientOptions: ClientOption[] = [];
    private selectedClient: ClientOption | null = null;
    private clientsLoading: boolean = false;
    private clientError: boolean = false;
    private clientSearchRequestId: number = 0;
    private searchTimer: any = null;

    private projectConnection: string = '';
    private projectConnectionError: boolean = false;

    private salesmenByGroup: { [groupId: number]: { id: string; name: string }[] } = {};
    private salesmenLoading: boolean = false;
    private assignedUserId: string | null = null;
    private extraCommercialist: { id: string; name: string } | null = null;
    private commercialistError: boolean = false;

    private canCreate: boolean | null = null;

    private get orderFormConfiguration() {
        return technologistOrderFormConfiguration;
    }

    private get systems() {
        return ProductSystemRepository.getAllShallow();
    }

    private get currentUser() {
        return UserRepository.getCurrentUser();
    }

    private get workFreeDays(): any[] {
        return this.$store.getters['tableData/workFreeDays'] ?? [];
    }

    private get eligibleGroupIds(): number[] {
        const allowedGroups = ((this.$store.getters['jwtData/allowedUserGroups'] ?? []) as any[]).map((id) => Number(id));
        const userRights = (this.$store.getters['jwtData/jwtData']?.userRights ?? {}) as any;

        return ROLTEK_GROUP_IDS.filter((groupId) => {
            if (!allowedGroups.includes(groupId)) {
                return false;
            }

            const rights = Object.keys(userRights[groupId] ?? {});

            return REQUIRED_ANY_RIGHT.some((right) => rights.includes(right));
        });
    }

    private get visibleClientOptions(): ClientOption[] {
        if (this.selectedClient && !this.clientOptions.some((option) => option.key === this.selectedClient!.key)) {
            return [this.selectedClient, ...this.clientOptions];
        }

        return this.clientOptions;
    }

    private groupName(groupId: number): string {
        if (groupId === 1) {
            return CommonUserGroups.ROLTEK;
        }

        if (groupId === 2) {
            return CommonUserGroups.ROLTEK_AT;
        }

        return String(groupId);
    }

    private async openPopup() {
        if (this.eligibleGroupIds.length === 0) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t(
                    "You do not have permission to create orders for this client's user group.",
                ) as string,
            });
            return;
        }

        this.loadingOverlay.start();

        try {
            await refreshTechnologistOrderFormOptions();

            if (this.systems.length < 2) {
                await ProductSystem.getAll();
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.loadingOverlay.stop();
        }

        this.visible = true;
        this.setInitialFormValues();
        await this.setInitialDefaults();
        this.runClientSearch('');
    }

    private closePopup() {
        this.visible = false;
        this.formData = {};
        this.orders = [];
        this.errors = {};
        this.orderErrors = {};
        this.pendingColorTarget = null;
        this.clientOptions = [];
        this.selectedClient = null;
        this.clientsLoading = false;
        this.clientError = false;
        this.clientSearchRequestId++;
        this.projectConnection = '';
        this.projectConnectionError = false;
        this.salesmenByGroup = {};
        this.resetCommercialist();
        this.canCreate = null;

        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
    }

    private setInitialFormValues() {
        this.formData = technologistOrderFormConfiguration.formFields.reduce((acc: Record<string, any>, field) => {
            if (field.type === 'number') {
                acc[field.name as keyof TechnologistForm] = 0;
            } else if (field.type === 'textarea' || field.type === 'text') {
                acc[field.name as keyof TechnologistForm] = '';
            } else if (field.type === 'dropdown') {
                acc[field.name as keyof TechnologistForm] = null;
            } else if (field.type === 'attachment') {
                acc[field.name as keyof TechnologistForm] = [];
            } else if (field.type === 'date') {
                acc[field.name as keyof TechnologistForm] = null;
            }

            return acc;
        }, {} as Partial<TechnologistForm>);
    }

    private async loadWorkFreeDays() {
        if (this.workFreeDays.length) {
            return;
        }

        try {
            await NextStep.getWorkFreeDays();
        } catch (e) {
        }
    }

    private async setInitialDefaults() {
        this.orders = [
            { system: '', quantity: 0, attachmentRows: [{ file: null, attachmentType: null }], colorData: null },
        ] as any;
        (this.formData as any).numOfOrders = '1';

        await this.loadWorkFreeDays();
        (this.formData as any).orderAcceptedDate = moment();
        (this.formData as any).deliveryDate = getFirstAvailableDeliveryDate(this.workFreeDays);
    }


    private onClientSearch(value: string) {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }

        this.searchTimer = setTimeout(() => {
            this.runClientSearch(value || '');
        }, 300);
    }

    private toClientOption(client: any, userGroupId: number): ClientOption {
        const contactPersons = (client.contactPersons ?? []) as any[];
        const contactPerson = contactPersons.find((person: any) => person.isDefault) ?? contactPersons[0] ?? null;
        const city = client.address?.city ?? '';

        return {
            key: `${userGroupId}:${client.id}`,
            clientId: String(client.id),
            userGroupId,
            name: client.name ?? '',
            label: [client.name, city, this.groupName(userGroupId)].filter(Boolean).join(' · '),
            paymentTypeId: client.paymentType?.id ? String(client.paymentType.id) : null,
            assignedUserId: client.assignedUser?.id ? String(client.assignedUser.id) : null,
            assignedUserName: client.assignedUser?.name ?? '',
            contactPersonId: contactPerson ? String(contactPerson.id) : null,
        };
    }

    private async runClientSearch(query: string) {
        const requestId = ++this.clientSearchRequestId;
        this.clientsLoading = true;

        try {
            const resultsPerGroup = await Promise.all(
                this.eligibleGroupIds.map(async (groupId) => {
                    try {
                        const clients = await InternalOrderWithoutConfigurationAPI.searchClients(query, groupId);
                        return clients.map((client: any) => this.toClientOption(client, groupId));
                    } catch (e) {
                        console.error('Client search failed for user group', groupId, e);
                        return [] as ClientOption[];
                    }
                }),
            );

            if (requestId !== this.clientSearchRequestId) {
                return;
            }

            this.clientOptions = ([] as ClientOption[])
                .concat(...resultsPerGroup)
                .sort((a, b) => a.label.localeCompare(b.label));
        } finally {
            if (requestId === this.clientSearchRequestId) {
                this.clientsLoading = false;
            }
        }
    }

    private async onClientChange(key: string | undefined) {
        if (!key) {
            this.selectedClient = null;
            this.canCreate = null;
            this.resetCommercialist();
            return;
        }

        const option = this.visibleClientOptions.find((candidate) => candidate.key === key);

        if (!option) {
            return;
        }

        this.selectedClient = option;
        this.clientError = false;
        this.canCreate = null;
        await this.applyCommercialistDefault(option);

        await this.loadPrefill(option, true);
    }

    private resolveCommercialistName(option: ClientOption): string {
        if (option.assignedUserId) {
            return option.assignedUserName;
        }

        if ((this.currentUser as any)?.isSalesman) {
            return (this.currentUser as any)?.name ?? '';
        }

        return '';
    }

    private resolveAssignedUserId(option: ClientOption): string | null {
        if (option.assignedUserId) {
            return option.assignedUserId;
        }

        if ((this.currentUser as any)?.isSalesman && this.currentUser?.id) {
            return String(this.currentUser.id);
        }

        return null;
    }

    private get commercialistOptions(): { id: string; name: string }[] {
        const list = this.selectedClient ? this.salesmenByGroup[this.selectedClient.userGroupId] ?? [] : [];

        if (this.extraCommercialist && !list.some((user) => user.id === this.extraCommercialist!.id)) {
            return [this.extraCommercialist, ...list];
        }

        return list;
    }

    private resetCommercialist() {
        this.assignedUserId = null;
        this.extraCommercialist = null;
        this.commercialistError = false;
    }

    private async applyCommercialistDefault(option: ClientOption) {
        const defaultId = this.resolveAssignedUserId(option);

        this.commercialistError = false;
        this.assignedUserId = defaultId;
        this.extraCommercialist = defaultId ? { id: defaultId, name: this.resolveCommercialistName(option) } : null;

        await this.loadSalesmen(option.userGroupId);
    }

    private async loadSalesmen(userGroupId: number) {
        if (this.salesmenByGroup[userGroupId]) {
            return;
        }

        this.salesmenLoading = true;

        try {
            const users = await InternalOrderWithoutConfigurationAPI.getSalesmen(userGroupId);

            this.$set(
                this.salesmenByGroup,
                userGroupId,
                users
                    .map((user: any) => ({ id: String(user.id), name: user.name ?? '' }))
                    .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)),
            );
        } catch (e) {
            console.error('Could not load the commercialists for user group', userGroupId, e);
        } finally {
            this.salesmenLoading = false;
        }
    }

    private async loadPrefill(option: ClientOption, applyValues: boolean) {
        try {
            const prefill = await InternalOrderWithoutConfigurationAPI.getPrefill(option.clientId, option.userGroupId);

            if (!this.selectedClient || this.selectedClient.key !== option.key) {
                return;
            }

            this.canCreate = prefill.canCreate === true;

            if (applyValues) {
                // The order type depends on the client's group, so it always follows the selected client.
                if (prefill.documentType) {
                    (this.formData as any).documentType = prefill.documentType;
                }

                // The technologist is the global round-robin "next in line"; do not override a manual choice.
                if (prefill.technologistName && !(this.formData as any).technologistName) {
                    (this.formData as any).technologistName = prefill.technologistName;
                }
            }

            if (this.canCreate === false) {
                this.$notification.error({
                    message: this.$t('Error') as string,
                    description: this.$t(
                        "You do not have permission to create orders for this client's user group.",
                    ) as string,
                });
            }
        } catch (e) {
            this.canCreate = null;
            console.error('Could not load the prefill for the selected client', e);
        }
    }

    private openColorPicker(payload: { target: 'general' | number }) {
        this.pendingColorTarget = payload.target;
        EventBus.$emit(EventBusEvents.showColorModule, {
            colorSystemPID: 'material',
            fieldValue: '',
            activeProductFormId: null,
            productName: '',
        });
    }

    private onColorSelected(payload: { color: any; selectedPowder: any }) {
        if (this.pendingColorTarget === null) {
            return;
        }

        const colorData = {
            colorName: payload.color?.name ?? null,
            colorHex: payload.color?.hex ?? null,
            powderId: payload.selectedPowder?.id ? String(payload.selectedPowder.id) : null,
            powderName: payload.selectedPowder?.name ?? null,
            ingeniousColor: payload.color?.ingeniousColor ?? null,
        };

        if (this.pendingColorTarget === 'general') {
            this.$set(this.formData, 'colorData', colorData);
        } else {
            this.$set(this.orders[this.pendingColorTarget], 'colorData', colorData);
        }

        this.pendingColorTarget = null;
    }

    private addOrder() {
        const order: any = {
            system: '',
            quantity: 0,
            attachmentRows: [{ file: null, attachmentType: null }],
            colorData: null,
        };

        this.orders.push(order);
        this.formData.numOfOrders = this.orders.length.toString();
    }

    private deleteOrder(index: number) {
        this.orders.splice(index, 1);
        this.formData.numOfOrders = this.orders.length.toString();
        this.orderErrors.hasOwnProperty(index.toString()) && this.$delete(this.orderErrors, index.toString());
    }

    private setInitialOrders() {
        if (!this.formData.numOfOrders) {
            return;
        }

        this.orders = Array.from({ length: parseInt(this.formData.numOfOrders) }, () => ({
            system: '',
            quantity: 0,
            attachmentRows: [{ file: null, attachmentType: null }],
            colorData: null,
        })) as any;
    }

    private validateField(fieldOrName: TechnologistFormField | string) {
        const field = (typeof fieldOrName === 'string'
            ? this.orderFormConfiguration.formFields.find((candidate) => candidate.name === fieldOrName)
            : fieldOrName) as TechnologistFormField | undefined;

        if (!field) {
            this.$delete(this.errors, String(fieldOrName));
            return;
        }

        if (!field.required) {
            this.$delete(this.errors, field.name);
            return;
        }

        const value = this.formData[field.name as keyof TechnologistForm];
        let isValid = true;

        if (field.type === 'number' && value === 0) {
            isValid = false;
        } else if ((field.type === 'text' || field.type === 'textarea') && typeof value === 'string' && !value.trim()) {
            isValid = false;
        } else if ((field.type === 'dropdown' || field.type === 'date') && (value === null || value === undefined)) {
            isValid = false;
        } else if (field.type === 'attachment' && Array.isArray(value) && value.length === 0) {
            isValid = false;
        }

        if (!isValid) {
            this.$set(this.errors, field.name, `${this.$t(field?.errorFieldName ?? field.label)}`);
        } else {
            this.$delete(this.errors, field.name);
        }
    }

    private hasAttachmentWithoutType(): boolean {
        const missingType = (row: any) => row.file && !row.existingAttachmentId && !row.attachmentType;
        const orderLevelRows = (this.formData as any).attachmentRows ?? [];

        return (
            orderLevelRows.some(missingType) ||
            this.orders.some((order: any) => (order.attachmentRows ?? []).some(missingType))
        );
    }

    private validateForm(): boolean {
        this.orderFormConfiguration.formFields.forEach((field) => {
            if (field.required) {
                this.validateField(field as TechnologistFormField);
            }
        });

        if (!hasCommentOrAttachment(this.formData)) {
            this.$set(this.errors, 'commentOrAttachment', this.$t('Please add at least a comment or an attachment.') as string);
        } else {
            this.$delete(this.errors, 'commentOrAttachment');
        }

        if (this.hasAttachmentWithoutType()) {
            this.$set(this.errors, 'attachmentType', this.$t('Attachment type') as string);
        } else {
            this.$delete(this.errors, 'attachmentType');
        }

        let ordersValid = true;

        this.orders.forEach((order, i) => {
            if (!order.system || order.quantity === 0) {
                ordersValid = false;
                this.$set(this.orderErrors, i.toString(), {
                    system: !order.system,
                    quantity: order.quantity === 0,
                });
            } else {
                this.$delete(this.orderErrors, i.toString());
            }
        });

        if (!ordersValid) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Please fill all orders.') as string,
            });
            return false;
        }

        if (Object.keys(this.errors).length > 0) {
            const descriptionMessage = this.$t('Following fields are missing:') as string;
            const errorFields = Object.values(this.errors).join('\n-');

            this.$notification.error({
                message: this.$t('Error') as string,
                description: `${descriptionMessage}\n-${errorFields}`,
            });
            return false;
        }

        return true;
    }

    private async validateAll(): Promise<boolean> {
        if (!this.selectedClient) {
            this.clientError = true;
            this.$notification.error({ message: this.$t('Nedostaju podaci') as string, description: '' });
            return false;
        }

        if (!this.selectedClient.contactPersonId) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t('The selected client has no contact person.') as string,
            });
            return false;
        }

        if (!this.assignedUserId) {
            this.commercialistError = true;
            this.$notification.error({ message: this.$t('Nedostaju podaci') as string, description: '' });
            return false;
        }

        const trimmedConnection = this.projectConnection.trim();

        if (trimmedConnection && !projectConnectionNameRegex.test(trimmedConnection)) {
            this.projectConnectionError = true;
            this.$notification.error({
                message: this.$t('Invalid project connection name') as string,
                description: '',
            });
            return false;
        }

        if (this.orders.length === 0) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Please add at least one order.') as string,
            });
            return false;
        }

        // The prefill carries the backend permission check, so it has to be known before anything is created.
        if (this.canCreate === null) {
            await this.loadPrefill(this.selectedClient, false);
        }

        if (this.canCreate === null) {
            this.$notification.error({ message: this.$t('Dogodila se greška') as string, description: '' });
            return false;
        }

        if (this.canCreate === false) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t(
                    "You do not have permission to create orders for this client's user group.",
                ) as string,
            });
            return false;
        }

        return this.validateForm();
    }

    private async submitForm() {
        if (this.submitting) {
            return;
        }

        if (!(await this.validateAll())) {
            return;
        }

        const client = this.selectedClient as ClientOption;
        const userGroupId = client.userGroupId;
        let projectId: string | null = null;
        let shouldClose = false;

        this.submitting = true;
        this.loadingOverlay.start();

        try {
            const projectResponse = await InternalOrderWithoutConfigurationAPI.createProject(
                {
                    clientId: client.clientId,
                    contactPersonId: client.contactPersonId as string,
                    assignedUserId: this.assignedUserId,
                    madeByUserId: this.currentUser?.id ? String(this.currentUser.id) : null,
                    connection: this.projectConnection.trim() || null,
                },
                userGroupId,
            );

            if (!projectResponse?.data?.id) {
                throw new Error('The project was not created.');
            }

            projectId = String(projectResponse.data.id);

            shouldClose = true;

            const offerResponse = await InternalOrderWithoutConfigurationAPI.createOffer(
                projectId,
                client.paymentTypeId,
                userGroupId,
            );
            const offerId = String(offerResponse?.data?.id);

            const formattedFormData = prepareTechnologistOrderFormData(this.formData);

            if ((this.formData as any).colorData) {
                (formattedFormData as any).technologistOrderColorData = (this.formData as any).colorData;
            }

            const technologistOrderResponse = await InternalOrderWithoutConfigurationAPI.createTechnologistOrder(
                formattedFormData,
                offerId,
                userGroupId,
            );
            const technologistOrderId = String(technologistOrderResponse?.data?.id);

            await this.uploadAttachments(technologistOrderId, userGroupId);

            await this.createOrders(projectId, offerId, userGroupId);

            await InternalOrderWithoutConfigurationAPI.orderByTechnologist(offerId, userGroupId);

            this.$notification.success({
                message: this.$t('Success') as string,
                description: `${this.$t('Order created for project')} ${projectId}`,
            });
            shouldClose = true;
        } catch (err) {
            console.error('Error creating order without configuration', err);

            if (projectId) {
                this.$notification.error({
                    message: this.$t('Error') as string,
                    description: `${this.$t(
                        'The order could not be completed. The project was already created:',
                    )} ${projectId}`,
                    duration: 0,
                });
            } else {
                this.$notification.error({ message: this.$t('Dogodila se greška') as string, description: '' });
            }
        } finally {
            this.submitting = false;
            this.loadingOverlay.stop();

            if (shouldClose) {
                this.closePopup();
            }
        }
    }

    private async uploadAttachments(technologistOrderId: string, userGroupId: number) {
        const attachmentRows = (this.formData as any).attachmentRows ?? [];

        for (const row of attachmentRows) {
            const file = row.file?.originFileObj;

            if (!file) {
                continue;
            }

            await InternalOrderWithoutConfigurationAPI.uploadTechnologistAttachment(
                technologistOrderId,
                file,
                row.attachmentType ?? null,
                userGroupId,
            );
        }
    }

    private async createOrders(projectId: string, offerId: string, userGroupId: number) {
        for (const order of this.orders as any[]) {
            const response = await InternalOrderWithoutConfigurationAPI.createInternalOrder(
                { system: order.system, quantity: order.quantity, projectId, offerId },
                userGroupId,
            );
            const internalOrderId = response?.data?.id;

            if (!internalOrderId) {
                throw new Error('A sub order was not created.');
            }

            if (order.colorData) {
                await InternalOrderWithoutConfigurationAPI.updateInternalOrderColor(
                    String(internalOrderId),
                    order.colorData,
                    userGroupId,
                );
            }

            for (const row of order.attachmentRows ?? []) {
                const file = row.file?.originFileObj;

                if (!file) {
                    continue;
                }

                await InternalOrderWithoutConfigurationAPI.uploadInternalOrderAttachment(
                    String(internalOrderId),
                    file,
                    row.attachmentType ?? null,
                    userGroupId,
                );
            }
        }
    }

    private created() {
        EventBus.$on(EventBusEvents.showInternalOrderWithoutConfigurationPopup, this.openPopup);
        EventBus.$on(EventBusEvents.selectMaterialColor, this.onColorSelected);
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.showInternalOrderWithoutConfigurationPopup, this.openPopup);
        EventBus.$off(EventBusEvents.selectMaterialColor, this.onColorSelected);

        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
    }
}
